import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, TableSkeleton } from "@/components/shared";
import SubmitCandidateDialog from "@/components/submissions/SubmitCandidateDialog";
import { label, initials, timeAgo } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const STAGES = ["sourced", "contacted", "screening", "qualified", "submitted", "client_review",
  "interview_scheduled", "interview_completed", "final_interview", "offer_pending",
  "offer_extended", "offer_accepted", "hired", "rejected", "withdrawn", "on_hold"];
const REASON_STAGES = ["rejected", "withdrawn"];

export default function Pipeline() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [byStage, setByStage] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [reasonModal, setReasonModal] = useState(null); // {sub, stage}
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/submissions", { params: { page_size: 200 } });
      const map = {}; STAGES.forEach((s) => (map[s] = []));
      (data.items || []).forEach((s) => { (map[s.stage] = map[s.stage] || []).push(s); });
      setByStage(map);
    } catch { setByStage({}); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const canMove = can("submission:create") || can("pipeline:view");

  const applyStage = async (sub, stage, reasonText) => {
    // optimistic
    setByStage((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { next[k] = next[k].filter((x) => x.id !== sub.id); });
      next[stage] = [{ ...sub, stage }, ...(next[stage] || [])];
      return next;
    });
    try { await api.put(`/submissions/${sub.id}/stage`, { stage, reason: reasonText || null }); toast.success(`Moved to ${label(stage)}`); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };

  const onDrop = (stage) => {
    if (!dragId) return;
    let sub = null;
    Object.values(byStage).forEach((arr) => arr.forEach((x) => { if (x.id === dragId) sub = x; }));
    setDragId(null);
    if (!sub || sub.stage === stage) return;
    if (REASON_STAGES.includes(stage)) { setReasonModal({ sub, stage }); setReason(""); return; }
    applyStage(sub, stage);
  };

  if (!byStage) return <div><PageHeader title="Pipeline" subtitle="Loading…" /><TableSkeleton /></div>;

  const visible = STAGES.filter((s) => (byStage[s] || []).length > 0 ||
    ["sourced", "submitted", "client_review", "interview_scheduled", "offer_extended", "hired", "rejected"].includes(s));

  return (
    <div>
      <PageHeader title="Recruitment Pipeline" subtitle="Drag candidate cards between stages. Rejections and withdrawals require a reason."
        actions={can("submission:create") && <Button size="sm" onClick={() => setSubmitOpen(true)} data-testid="pipeline-add-candidate" className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"><Plus className="h-4 w-4" /> Submit Candidate</Button>} />
      <div className="flex gap-4 overflow-x-auto scrollbar-thin pb-4" data-testid="pipeline-board">
        {visible.map((stage) => {
          const cards = byStage[stage] || [];
          return (
            <div key={stage} data-testid={`pipeline-stage-${stage}`} className="w-72 shrink-0"
              onDragOver={(e) => { if (canMove) e.preventDefault(); }} onDrop={() => onDrop(stage)}>
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label(stage)}</span>
                <span className="text-xs font-semibold h-5 min-w-5 px-1.5 rounded-full bg-accent grid place-items-center">{cards.length}</span>
              </div>
              <div className="space-y-2 min-h-[120px] rounded-xl bg-muted/40 p-2 transition-colors">
                {cards.map((c) => (
                  <Card key={c.id} draggable={canMove} onDragStart={() => setDragId(c.id)} onDragEnd={() => setDragId(null)}
                    data-testid={`pipeline-card-${c.id}`}
                    className={`p-3 hover:shadow-md transition-shadow ${canMove ? "cursor-grab active:cursor-grabbing" : ""} ${dragId === c.id ? "opacity-50" : ""}`}
                    onClick={() => navigate(`/candidates/${c.candidate_id}`)}>
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-8 w-8"><AvatarFallback className="bg-[#255058] text-white text-[10px]">{initials(c.candidate_name)}</AvatarFallback></Avatar>
                      <div className="min-w-0"><div className="text-sm font-medium truncate">{c.candidate_name}</div><div className="text-xs text-muted-foreground truncate">{c.job_title}</div></div>
                    </div>
                    <div className="mt-2 text-[11px] text-muted-foreground truncate">{c.client_name} · {timeAgo(c.last_activity_at)}</div>
                  </Card>
                ))}
                {cards.length === 0 && <div className="text-[11px] text-muted-foreground text-center py-4">Drop here</div>}
              </div>
            </div>
          );
        })}
      </div>

      <SubmitCandidateDialog open={submitOpen} onClose={() => setSubmitOpen(false)} onDone={() => { setSubmitOpen(false); load(); }} />

      <Dialog open={!!reasonModal} onOpenChange={(o) => !o && setReasonModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reason required</DialogTitle><DialogDescription>Provide a reason to mark this candidate as {reasonModal && label(reasonModal.stage)}. This is recorded in the audit trail.</DialogDescription></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason…" data-testid="pipeline-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReasonModal(null)}>Cancel</Button>
            <Button className="bg-[#255058] hover:bg-[#1c3d43] text-white" data-testid="pipeline-reason-confirm"
              onClick={() => { if (!reason.trim()) { toast.error("Reason is required"); return; } applyStage(reasonModal.sub, reasonModal.stage, reason); setReasonModal(null); }}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
